<template>
  <div>
    <div class="actions">
      <slot name="actions" />
      <input id="showAcknowledged" class="mr-1 leading-tight" type="checkbox" v-model="showAcknowledged">
      <label for="showAcknowledged" class="text-grey-darker font-medium">
        <span class="text-sm uppercase">
          Show Acknowledged
        </span>
      </label>
    </div>
    <div>
      <h2 class="mt-4 font-medium">#{{ listing.id }} - {{ listing.job_title }}</h2>
      <h3 class="mt-1 font-medium">Applications</h3>
    </div>
    <div class="applications flex flex-wrap -mx-4">
      <div
        v-for="application in applications"
        :key="application.id"
        class="w-full lg:w-1/2 pb-8"
      >
        <div class="recruitment-card">
          <div class="flex justify-between items-start">
            <h3 class="font-medium capitalize">
              {{ application.title }} {{ application.first_name }} {{ application.surname }}
            </h3>
            <small>#{{ application.id }}</small>
          </div>
          <div class="mt-4">
            <div class="recruitment-card__label-wrapper">
              <div class="recruitment-card__label">Available to start on:</div>
              <div class="mx-1">{{ moment(application.start_date).format('MMM Do, YYYY') }}</div>
            </div>
            <div class="recruitment-card__label-wrapper">
              <div class="recruitment-card__label">Email:</div>
              <div class="mx-1">{{ application.email }}</div>
            </div>
            <div class="recruitment-card__label-wrapper">
              <div class="recruitment-card__label">Phone:</div>
              <div class="mx-1">{{ application.phone }}</div>
            </div>
            <div class="recruitment-card__label-wrapper">
              <div class="recruitment-card__label">Address:</div>
              <div class="mx-1">{{ buildAddress(application) }}</div>
            </div>
            <div class="recruitment-card__label-wrapper">
              <div class="recruitment-card__label">Home Town:</div>
              <div class="mx-1">{{ application.home_town }}</div>
            </div>
            <div class="recruitment-card__label-wrapper">
              <div class="recruitment-card__label">LinekedIn:</div>
              <div class="mx-1">{{ application.linkedin_url }}</div>
            </div>
            <div class="recruitment-card__label-wrapper">
              <div class="recruitment-card__label">Has Driving License:</div>
              <!-- <div class="mx-1">{{ application.has_driving_licence ? 'Yes' : 'No' }}</div> -->
              <i v-if="application.has_driving_licence" class="mx-1 fas fa-check"></i>
              <i v-else class="mx-1 fas fa-times"></i>
            </div>
          </div>
          <div class="mt-4" v-if="application.notes">
            <div class="text-sm">Notes:</div>
            <div class="text-sm leading-tight">{{ application.notes }}</div>
          </div>
          <div class="mt-auto flex">
            <a
              v-if="application.cover_letter_url"
              :href="application.cover_letter_url"
              target="_blank"
              class="inline-block recruitment-button recruitment-button--small border border-tertiary text-tertiary hover:text-white hover:bg-tertiary"
            >
              <i class="mr-1 fas fa-file-download" />
              Cover Letter
            </a>
            <a
              v-if="application.cv_url"
              :href="application.cv_url"
              target="_blank"
              class="inline-block recruitment-button recruitment-button--small border border-tertiary text-tertiary hover:text-white hover:bg-tertiary"
            >
              <i class="mr-1 fas fa-file-download" />
              Curriculum Vitae
            </a>
            <button
              v-if="!application.acknowledged || application.acknowledging"
              class="recruitment-button recruitment-button--small border border-green-dark text-green-dark ml-auto mr-0"
              :class="{ 'opacity-50 cursor-default': application.acknowledging, 'hover:text-white hover:bg-green-dark': !application.acknowledging }"
              :disabled="application.acknowledging"
              @click="acknowledge(application)"
            >
              <i v-if="application.acknowledging" class="mr-1 fas fa-circle-notch fa-spin" />
              <i v-else class="mr-1 fas fa-check" />
              Acknowledge
            </button>
          </div>
          <div v-if="hasAdditionalDocuments(application)" class="block mt-4 border-t mx-3" />
          <div v-if="hasAdditionalDocuments(application)" class="mt-4 -mb-3 -mx-1 flex flex-wrap">
            <div
              v-for="(otherDocument, index) in application.other_document_urls"
              :key="index"
              class="w-1/4 px-1"
            >
              <a
                :href="otherDocument"
                target="_blank"
                class="inline-block recruitment-button recruitment-button--small border border-primary text-primary hover:text-white hover:bg-primary w-full mb-3"
              >
                <i class="mr-1 fas fa-file-download" />
                Additional #{{ index + 1 }}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { v2Api } from '@/http'
import moment from 'moment'
import { mapState, mapActions } from 'vuex'

export default {
  props: [ 'listing' ],

  data () {
    return {
      showAcknowledged: true
    }
  },

  computed: {
    ...mapState([ 'profile', 'selectedOffice' ]),

    applications () {
      if (this.showAcknowledged) {
        return this.listing.applications
      }

      return this.listing.applications.filter(application => !application.acknowledged)
    },

    isAdmin () {
      return this.profile.type <= 2
    }
  },

  methods: {
    ...mapActions([ 'displayToastNotification' ]),

    moment: moment,

    acknowledge (application) {
      this.$set(application, 'acknowledging', true)

      application.acknowledged = true

      v2Api
        .post(`/recruitment-listings/${this.listing.id}/applications/${application.id}/acknowledge`)
        .then(response => {
          application.acknowledging = false
          application = response.data
        })
        .catch(() => {
          application.acknowledged = false
          application.acknowledging = false
        })
    },

    hasAdditionalDocuments (application) {
      return application.other_document_urls.length > 0
    },

    buildAddress (application) {
      return [
        application.address_name_number,
        application.address_line2,
        application.address_line3,
        application.address_town,
        application.address_line4,
        application.address_postcode
      ].filter(line => line && line !== '').join(', ')
    }
  }
}
</script>

<style lang="sass" scoped>
.recruitment-card
  @apply flex flex-col bg-white rounded-lg m-4 p-4 leading-normal h-full
  &__label-wrapper
    @apply flex items-center -mx-1
  &__label
    @apply mx-1 w-1/4 text-right text-sm

table
  @apply table-auto w-full
  thead
    @apply border-b uppercase text-xs
    th
      @apply pb-2 font-semibold
  tbody
    @apply text-sm capitalize
    tr
      @apply border-b
      td
        @apply py-4 align-middle
</style>
